import * as React from 'react';

interface ConsumerProps<A> {
  children: (args: A) => React.ReactNode;
}

interface ProviderProps<A> {
  value: A;
}

function createRequiredContext<A>() {
  const Context = React.createContext<A | undefined>(undefined);

  const RequiredContextProvider: React.SFC<ProviderProps<A>> = ({
    value,
    children,
  }) => <Context.Provider value={value}>{children}</Context.Provider>;

  const RequiredContextConsumer: React.SFC<ConsumerProps<A>> = ({
    children,
  }) => (
    <Context.Consumer>
      {value => {
        if (!value) {
          throw new Error('Value is required');
        }
        return children(value);
      }}
    </Context.Consumer>
  );

  const useRequiredContext = () => {
    const value = React.useContext(Context);

    if (!value) {
      throw new Error('Value is required');
    }

    return value;
  };

  return {
    Provider: RequiredContextProvider,
    Consumer: RequiredContextConsumer,
    useContext: useRequiredContext,
  };
}

export default createRequiredContext;
